import type { AssistantAudience } from './assistantCapabilities';
import {
  explainInvoice,
  invoicePageLink,
  invoiceTypeLabel,
  isRefund,
  statusLabelFor,
} from './invoiceStatus';
import type { KnowledgeAnswer, KnowledgeBreakdownRow, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';
import { breakdownRows, displayDate, displayDateRange, enumLabel, isForbidden, money } from './lookupFormat';
import { claimsLookup, extractInvoiceNumber } from './lookupRouting';
import { findInvoiceByNumber, type InvoiceLookupRow } from './requests/invoice-lookup-requests';

/**
 * Looks up one invoice by its number and says where it stands.
 *
 * ## Why this is a skill and not left to the backend agent
 *
 * "What's going on with invoice 1234567" is the question staff field most when
 * a customer calls about money, and the answer is a join the invoice report
 * already made: the charge, both parties, the dates, and a status that has to be
 * read three ways (buyer, supplier, raw). The agent can reach the same rows, but
 * it has to know that the invoice number is `transactionNumber`, that the search
 * matches it partially, and what each status means — and when it guesses wrong
 * the user gets a confident answer about a different invoice. A deterministic
 * lookup is exact and costs one request.
 *
 * ## What each audience sees
 *
 * The same row answers all three, scoped by the backend to what the caller may
 * read. A buyer hears about their charge and payment, a supplier about their
 * payout, and staff get every field the investigation needs — fees, processor
 * messages, status notes, refunds — plus which step stalled when a supplier
 * has not been paid.
 */

/** Any standalone number long enough to be an invoice number. */
const BARE_NUMBER = /\b(\d{5,})\b/;

/**
 * The invoice number: labelled ("invoice #1234567") when routed by it, or any
 * long number when the phrasing picked this skill ("invoice lookup 1234567").
 */
const invoiceNumberIn = (message: string): string | null =>
  extractInvoiceNumber(message) ?? message.match(BARE_NUMBER)?.[1] ?? null;

const lead = (row: InvoiceLookupRow, number: string, audience: AssistantAudience): string => {
  const type = invoiceTypeLabel(row);
  const booking = row.bookingNumber ? ` on booking **#${row.bookingNumber}**` : '';
  const at = row.locationName ? ` at **${row.locationName}**` : '';
  const service = displayDateRange(row.serviceStartDate ?? row.startDate, row.serviceEndDate ?? row.endDate);
  const period = service ? `, for ${service}` : '';

  if (audience === 'supplier') {
    const amount = money(row.supplierAmount);
    const customer = row.buyerCompanyName ? ` from **${row.buyerCompanyName}**` : '';
    return `**Invoice #${number}** is a **${type}**${customer}${booking}${at}${period}${
      amount && !isRefund(row) ? ` \u2014 **${amount}** to you` : ''
    }.`;
  }

  const amount = money(row.buyerAmount);
  const amountPhrase = amount ? ` of **${amount}**` : '';
  if (audience === 'buyer') {
    return `**Invoice #${number}** is a **${type}**${amountPhrase}${booking}${at}${period}.`;
  }

  const buyer = row.buyerCompanyName ? ` to **${row.buyerCompanyName}**` : '';
  const supplier = row.supplierCompanyName ? ` (${row.supplierCompanyName})` : '';
  return `**Invoice #${number}** is a **${type}**${amountPhrase}${buyer}${booking}${at}${supplier}${period}.`;
};

const refundNote = (row: InvoiceLookupRow): string => {
  const parts: string[] = [];
  if (row.refundForTransactionNumber) {
    parts.push(`It refunds invoice **#${row.refundForTransactionNumber}**.`);
  }
  if (row.refundTransactionNumbers?.trim()) {
    parts.push(`It has been refunded by invoice **#${row.refundTransactionNumbers.trim()}**.`);
  }
  return parts.join(' ');
};

const buyerRows = (row: InvoiceLookupRow, number: string): KnowledgeBreakdownRow[] =>
  breakdownRows([
    ['Invoice', `#${number}`],
    ['Type', invoiceTypeLabel(row)],
    ['Booking', row.bookingNumber && `#${row.bookingNumber}`],
    ['Location', [row.locationName, row.supplierCompanyName].filter(Boolean).join(' \u2014 ')],
    ['Service dates', displayDateRange(row.serviceStartDate ?? row.startDate, row.serviceEndDate ?? row.endDate)],
    ['Amount', money(row.buyerAmount)],
    ['Tax', row.transactionTaxAmount ? money(row.transactionTaxAmount) : ''],
    ['Status', statusLabelFor(row, 'buyer')],
    ['Payment method', row.paymentMethodDescription],
    ['Invoiced', displayDate(row.createdOn)],
    ['Payment started', displayDate(row.paymentCreatedOn)],
    ['Payment completed', displayDate(row.paymentCompletedOn)],
    ['Refund of invoice', row.refundForTransactionNumber && `#${row.refundForTransactionNumber}`],
    ['Refunded by invoice', row.refundTransactionNumbers && `#${row.refundTransactionNumbers}`],
    ['Refund reason', row.reasonForRefund],
  ]);

const supplierRows = (row: InvoiceLookupRow, number: string): KnowledgeBreakdownRow[] =>
  breakdownRows([
    ['Invoice', `#${number}`],
    ['Type', invoiceTypeLabel(row)],
    ['Booking', row.bookingNumber && `#${row.bookingNumber}`],
    ['Customer', row.buyerCompanyName],
    ['Location', row.locationName],
    ['Service dates', displayDateRange(row.serviceStartDate ?? row.startDate, row.serviceEndDate ?? row.endDate)],
    ['Your payout', money(row.supplierAmount)],
    ['Status', statusLabelFor(row, 'supplier')],
    ['Invoiced', displayDate(row.createdOn)],
    ['Customer paid', displayDate(row.paymentCompletedOn)],
    ['Payout started', displayDate(row.payoutCreatedOn)],
    ['Paid out', displayDate(row.payoutCompletedOn)],
    ['Payout on hold', row.supplierPayoutOnHold ? 'Yes' : ''],
    ['Refund of invoice', row.refundForTransactionNumber && `#${row.refundForTransactionNumber}`],
    ['Refunded by invoice', row.refundTransactionNumbers && `#${row.refundTransactionNumbers}`],
  ]);

const adminRows = (row: InvoiceLookupRow, number: string): KnowledgeBreakdownRow[] =>
  breakdownRows([
    ['Invoice', `#${number}`],
    ['Type', invoiceTypeLabel(row)],
    ['Booking', row.bookingNumber && `#${row.bookingNumber}`],
    ['Booking status', enumLabel(row.bookingStatus)],
    ['Buyer', row.buyerCompanyName],
    ['Supplier', row.supplierCompanyName],
    ['Location', row.locationName],
    ['Service dates', displayDateRange(row.serviceStartDate ?? row.startDate, row.serviceEndDate ?? row.endDate)],
    ['Buyer amount', money(row.buyerAmount)],
    ['Supplier payout', money(row.supplierAmount)],
    ['SecurSpace fees', money(row.securspaceFees)],
    ['Processor fees', money(row.paymentProcessorFees)],
    ['Tax', row.transactionTaxAmount ? money(row.transactionTaxAmount) : ''],
    ['Status', statusLabelFor(row, 'admin')],
    ['Buyer sees', statusLabelFor(row, 'buyer')],
    ['Supplier sees', statusLabelFor(row, 'supplier')],
    [
      'Payment method',
      [row.paymentMethodDescription, enumLabel(row.paymentProcessor)].filter(Boolean).join(' \u2014 '),
    ],
    ['Invoiced', displayDate(row.createdOn)],
    ['Payment started', displayDate(row.paymentCreatedOn)],
    ['Payment completed', displayDate(row.paymentCompletedOn)],
    ['Paid manually', displayDate(row.manualPaymentCompletedOn)],
    ['Payout started', displayDate(row.payoutCreatedOn)],
    ['Payout completed', displayDate(row.payoutCompletedOn)],
    ['Payout on hold', row.supplierPayoutOnHold ? 'Yes' : ''],
    ['Refund of invoice', row.refundForTransactionNumber && `#${row.refundForTransactionNumber}`],
    ['Refunded by invoice', row.refundTransactionNumbers && `#${row.refundTransactionNumbers}`],
    ['Refund reason', row.reasonForRefund],
    ['Processor message', row.paymentMessage],
    ['Status notes', row.statusNotes],
  ]);

const ROWS_BY_AUDIENCE: Record<AssistantAudience, (row: InvoiceLookupRow, number: string) => KnowledgeBreakdownRow[]> = {
  buyer: buyerRows,
  supplier: supplierRows,
  admin: adminRows,
};

const WHOSE: Record<AssistantAudience, string> = {
  buyer: 'on your account',
  supplier: 'on your account',
  admin: 'on SecurSpace',
};

export const answerInvoiceLookup = async ({ message, audience }: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const number = invoiceNumberIn(message);
  if (!number) {
    return {
      text:
        'Which invoice do you mean? Tell me its invoice number \u2014 for example ' +
        '_"look up invoice 1234567"_ \u2014 and I will check its status, amounts and payment.',
    };
  }

  let row: InvoiceLookupRow | null;
  try {
    row = await findInvoiceByNumber(audience, number);
  } catch (err) {
    if (isForbidden(err)) {
      return {
        text:
          'Your user role cannot view invoices, so I cannot look that one up for you. An account ' +
          'owner can, or SecurSpace support can help.',
      };
    }
    throw err;
  }

  if (!row) {
    return {
      text:
        `I could not find invoice **#${number}** ${WHOSE[audience]}. Double-check the number \u2014 ` +
        'it is the **Invoice Number** on the Invoices page, not the booking number. If it is a ' +
        'booking number, ask me about _"booking ' + number + '"_ instead.',
    };
  }

  const refunds = refundNote(row);
  return {
    text: [lead(row, number, audience), explainInvoice(row, audience), refunds].filter(Boolean).join(' '),
    breakdown: ROWS_BY_AUDIENCE[audience](row, number),
    link: invoicePageLink(audience, { invoiceNumber: number }, { from: row.createdOn, to: row.createdOn }),
  };
};

export const invoiceLookupQuery: KnowledgeQuery = {
  id: 'invoiceLookup',
  audiences: ['admin', 'supplier', 'buyer'],
  synonyms: [
    'look up an invoice',
    'lookup an invoice',
    'invoice lookup',
    'find an invoice',
    'check on an invoice',
    'status of an invoice',
    'details of an invoice',
    'investigate an invoice',
    'look up a specific invoice',
  ],
  claims: claimsLookup('invoice'),
  needsLocations: false,
  example: 'look up invoice 1234567',
  description:
    'Looks up one invoice by its invoice number (e.g. "look up invoice 1234567", "why hasn\u2019t ' +
    'invoice 1234567 been paid out", "was invoice 1234567 refunded") and explains where it ' +
    'stands: the charge, booking, parties, service dates, amounts, and what its status means ' +
    'for the person asking \u2014 payment for a buyer, payout for a supplier, and for staff the ' +
    'raw status, fees, processor message, notes, refunds and which step a payout stalled at. A ' +
    'diagnostic for one named invoice, not a count or total of invoices.',
  answer: answerInvoiceLookup,
};

export const __testing = { invoiceNumberIn };
